import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type {
	AdministratorSettingsPayload,
	IntakeDeletionReceipt,
	IntakeDetail,
	IntakeSummary,
	JsonValue,
	StaffBootstrap,
} from '../shared/types';
import { FIRM_NAME } from '../shared/firm';
import { consultationInputToIso } from '../shared/consultationTime';
import { canGeneratePdf } from '../shared/permissions';
import { canUseWordDocuments } from '../shared/wordDocuments';
import { WordDocuments } from './WordDocuments';
import { awaitDocumentSaves } from './documentSaveBarrier';
import { intakeStatusLabel } from '../shared/intakeStatus';
import { MessagePreviews } from './MessagePreviews';
import { api } from './api';
import { createConfirmation, friendlyCreateError } from './createFeedback';
import { revealFeedback } from './feedback';
import { NameInput, NameTextarea } from './NameInput';
import { pdfDeliveryLabel } from './pdfDeliveryLabel';
import { QuestionnaireEditor } from './QuestionnaireEditor';
import { NewIntakeForm, IntakeSettingsForm } from './IntakeForms';
import { reminderActionConfirmation, reminderStatusLabel } from './reminderStatus';
import { friendlyStaffError } from './staffFeedback';
import { staffIdentityLabel } from './staffIdentity';
import { CollapsiblePanel } from './CollapsiblePanel';

interface PlanningTrust {
	id?: string;
	referenceName?: string | null;
	trustType?: 'revocable' | 'irrevocable' | 'other' | null;
	fundingNotes?: string | null;
	trustees?: {
		primary?: { fullName?: string | null; address?: string | null };
		successor?: { fullName?: string | null; address?: string | null };
	};
	primaryBeneficiaries: Array<{ displayName: string; entityType: 'person' | 'entity' }>;
	successorBeneficiaries: Array<{ displayName: string; entityType: 'person' | 'entity' }>;
	contingentBeneficiaries: Array<{ displayName: string; entityType: 'person' | 'entity' }>;
}
export function StaffApp() {
	const [staff, setStaff] = useState<StaffBootstrap | null>(null);
	const [intakes, setIntakes] = useState<IntakeSummary[]>([]);
	const [archivedIntakes, setArchivedIntakes] = useState<IntakeSummary[]>([]);
	const [deletionReceipts, setDeletionReceipts] = useState<IntakeDeletionReceipt[]>([]);
	const [selected, setSelected] = useState<IntakeDetail | null>(null);
	const [planning, setPlanning] = useState<PlanningTrust[]>([]);
	const [message, setMessage] = useState('Signing in...');
	const [messageKind, setMessageKind] = useState<'status' | 'error' | 'confirmation'>('status');
	const [privateUrl, setPrivateUrl] = useState<string | null>(null);
	const [search, setSearch] = useState('');
	const [archivedSearch, setArchivedSearch] = useState('');
	const [intakeView, setIntakeView] = useState<'active' | 'archived'>('active');
	const [systemStatus, setSystemStatus] = useState<AdministratorSettingsPayload | null>(null);
	const intakeVersions = useRef(new Map<string, number>());
	const staffSaveChain = useRef<Promise<void>>(Promise.resolve());
	const failedAnswers = useRef(new Set<string>());
	const intakeSelection = useRef<{ id: string | null; loading?: boolean }>({ id: null });
	const messageRef = useRef<HTMLParagraphElement>(null);
	const privateLinkRef = useRef<HTMLDivElement>(null);
	const canCreateIntake = staff !== null && ['administrator', 'attorney', 'intake_staff'].includes(staff.role);
	function showMessage(text: string, kind: 'status' | 'error' | 'confirmation' = 'status') {
		setMessageKind(kind);
		setMessage(text);
	}

	const filteredIntakes = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return intakes;
		return intakes.filter((intake) =>
			[intake.clientDisplayName, intake.clientEmail, intake.clientMobile, intake.matterReference, intake.status, intake.consultationAt]
				.filter((value): value is string => Boolean(value))
				.some((value) => value.toLowerCase().includes(query)),
		);
	}, [intakes, search]);

	useEffect(() => {
		void api<StaffBootstrap>('/api/staff/bootstrap')
			.then((payload) => {
				setStaff(payload);
				showMessage('');
				return Promise.all([loadIntakes(), loadSystemStatus()]);
			})
			.catch((error: unknown) => showMessage(friendlyStaffError(error, 'Unable to sign in'), 'error'));
	}, []);

	useEffect(() => {
		if (messageKind === 'confirmation') revealFeedback(messageRef.current);
	}, [message, messageKind]);

	useEffect(() => {
		if (privateUrl) revealFeedback(privateLinkRef.current);
	}, [privateUrl]);

	async function loadIntakes() {
		const payload = await api<{ intakes: IntakeSummary[] }>('/api/staff/intakes');
		setIntakes(payload.intakes);
	}

	async function loadArchivedIntakes(query = archivedSearch) {
		const parameters = new URLSearchParams();
		if (query.trim()) parameters.set('q', query.trim());
		const payload = await api<{ intakes: IntakeSummary[] }>(
			`/api/staff/archived-intakes${parameters.size ? `?${parameters.toString()}` : ''}`,
		);
		setArchivedIntakes(payload.intakes);
	}

	async function loadDeletionReceipts() {
		const payload = await api<{ deletions: IntakeDeletionReceipt[] }>('/api/staff/intake-deletions');
		setDeletionReceipts(payload.deletions);
	}

	async function switchIntakeView(view: 'active' | 'archived') {
		intakeSelection.current = { id: null };
		setIntakeView(view);
		setSelected(null);
		setPlanning([]);
		setPrivateUrl(null);
		try {
			if (view === 'archived')
				await Promise.all([
					loadArchivedIntakes(),
					staff && (staff.role === 'administrator' || staff.role === 'attorney') ? loadDeletionReceipts() : Promise.resolve(),
				]);
			else await loadIntakes();
		} catch (error) {
			showMessage(friendlyStaffError(error, 'Intakes could not be loaded'), 'error');
		}
	}

	async function loadSystemStatus() {
		setSystemStatus(await api<AdministratorSettingsPayload>('/api/staff/status'));
	}

	async function openIntake(id: string, clearPrivateUrl = true) {
		const selection = { id, loading: true };
		intakeSelection.current = selection;
		showMessage('Loading intake...');
		if (clearPrivateUrl) setPrivateUrl(null);
		try {
			// Reload explicitly discards unsaved answers, but only after all writes settle.
			let pending: Promise<void>;
			do {
				pending = staffSaveChain.current;
				await pending.catch(() => undefined);
			} while (pending !== staffSaveChain.current);
			if (intakeSelection.current !== selection) return;
			const [detail, plan] = await Promise.all([
				api<IntakeDetail>(`/api/staff/intakes/${id}`),
				api<{ rowVersion: number; trusts: PlanningTrust[] }>(`/api/staff/intakes/${id}/consultation-planning`),
			]);
			if (intakeSelection.current !== selection) return;
			setSelected(detail);
			intakeVersions.current.set(id, detail.rowVersion);
			for (const key of failedAnswers.current) {
				if ((JSON.parse(key) as string[])[0] === id) failedAnswers.current.delete(key);
			}
			staffSaveChain.current = Promise.resolve();
			selection.loading = false;
			setPlanning(plan.trusts);
			showMessage('');
		} catch (error) {
			if (intakeSelection.current !== selection) return;
			selection.loading = false;
			showMessage(friendlyStaffError(error, 'Unable to open intake'), 'error');
		}
	}

	async function create(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!staff || !canCreateIntake) return;
		const form = event.currentTarget;
		const submitter = (event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
		const shouldSend = submitter?.value === 'send';
		const data = new FormData(form);
		const channels = [data.get('sendEmail') === 'on' ? 'email' : null, data.get('sendSms') === 'on' ? 'sms' : null].filter(
			(item): item is 'email' | 'sms' => item !== null,
		);
		const payload = {
			clientDisplayName: String(data.get('clientDisplayName') ?? ''),
			clientEmail: emptyToNull(data.get('clientEmail')),
			clientMobile: emptyToNull(data.get('clientMobile')),
			reminderLanguage: data.get('reminderLanguage') ?? 'en',
			deliveryChannels: channels,
			smsConsent: data.get('smsConsent') === 'on',
			smsConsentMethod: data.get('smsConsentMethod'),
			secondClientName: emptyToNull(data.get('secondClientName')),
			matterReference: emptyToNull(data.get('matterReference')),
		};
		try {
			showMessage('Creating intake...');
			const result = await api<{ id: string }>(
				'/api/staff/intakes',
				{
					method: 'POST',
					body: JSON.stringify({ ...payload, consultationAt: consultationInputToIso(String(data.get('consultationAt') ?? '')) }),
				},
				staff.csrfToken,
			);
			let createdPrivateUrl: string | null = null;
			if (shouldSend) {
				const link = await api<{ privateUrl: string }>(
					`/api/staff/intakes/${result.id}/send`,
					{ method: 'POST', body: '{}' },
					staff.csrfToken,
				);
				createdPrivateUrl = link.privateUrl;
			}
			form.reset();
			await loadIntakes();
			await openIntake(result.id);
			setPrivateUrl(createdPrivateUrl);
			showMessage(createConfirmation(shouldSend, channels, systemStatus), 'confirmation');
		} catch (error) {
			showMessage(friendlyCreateError(error), 'error');
		}
	}

	function staffAnswerChange(fieldId: string, value: JsonValue): Promise<void> {
		if (!selected || !staff) return Promise.resolve();
		const intakeId = selected.id;
		if (intakeSelection.current.id !== intakeId || intakeSelection.current.loading) return Promise.resolve();
		const answerKey = JSON.stringify([intakeId, fieldId]);
		setSelected((current) => (current ? { ...current, answers: { ...current.answers, [fieldId]: value } } : current));
		staffSaveChain.current = staffSaveChain.current
			.catch(() => undefined)
			.then(async () => {
				const result = await api<{ rowVersion: number; updatedAt: string }>(
					`/api/staff/intakes/${intakeId}/answers`,
					{
						method: 'PATCH',
						body: JSON.stringify({
							rowVersion: intakeVersions.current.get(intakeId) ?? selected.rowVersion,
							answers: { [fieldId]: value },
						}),
					},
					staff.csrfToken,
				);
				intakeVersions.current.set(intakeId, result.rowVersion);
				failedAnswers.current.delete(answerKey);
				setSelected((current) =>
					current?.id === intakeId ? { ...current, rowVersion: result.rowVersion, updatedAt: result.updatedAt } : current,
				);
				showMessage('Consultation answer saved.');
			})
			.catch((error: unknown) => {
				failedAnswers.current.add(answerKey);
				showMessage(friendlyStaffError(error, 'Answer was not saved'), 'error');
				throw error;
			});
		void staffSaveChain.current.catch(() => undefined);
		return staffSaveChain.current;
	}

	async function sendOrReplace() {
		if (!selected || !staff) return;
		try {
			const link = await api<{ privateUrl: string }>(
				`/api/staff/intakes/${selected.id}/send`,
				{ method: 'POST', body: '{}' },
				staff.csrfToken,
			);
			await openIntake(selected.id);
			setPrivateUrl(link.privateUrl);
			showMessage('New private link created and invitation queued. Previous links were revoked.', 'confirmation');
		} catch (error) {
			showMessage(friendlyStaffError(error, 'Unable to send link'), 'error');
		}
	}

	async function addText(path: 'notes' | 'coding-instructions', body: string) {
		if (!selected || !staff || !body.trim()) return;
		try {
			await api(`/api/staff/intakes/${selected.id}/${path}`, { method: 'POST', body: JSON.stringify({ body }) }, staff.csrfToken);
			await openIntake(selected.id);
			showMessage(path === 'notes' ? 'Consultation note added.' : 'Coding instructions added.', 'confirmation');
		} catch (error) {
			showMessage(friendlyStaffError(error, 'The information was not added'), 'error');
			throw error;
		}
	}

	async function prepareDocument(intakeId: string): Promise<() => boolean> {
		const selection = intakeSelection.current;
		const isCurrent = () => selection.id === intakeId && !selection.loading && intakeSelection.current === selection;
		await awaitDocumentSaves(staffSaveChain, isCurrent, () =>
			[...failedAnswers.current].some((key) => (JSON.parse(key) as string[])[0] === intakeId),
		);
		return isCurrent;
	}

	async function finalize() {
		if (!selected || !staff) return;
		const intakeId = selected.id;
		try {
			const isCurrent = await prepareDocument(intakeId);
			if (!isCurrent()) return;
			await api(`/api/staff/intakes/${intakeId}/finalize`, { method: 'POST', body: '{}' }, staff.csrfToken);
			if (!isCurrent()) return;
			showMessage('PDF generation queued. Refresh this intake shortly to download it.', 'confirmation');
		} catch (error) {
			showMessage(friendlyStaffError(error, 'PDF generation was not queued'), 'error');
		}
	}

	async function savePlanning() {
		if (!selected || !staff) return;
		try {
			await api(
				`/api/staff/intakes/${selected.id}/consultation-planning`,
				{
					method: 'PUT',
					body: JSON.stringify({ rowVersion: intakeVersions.current.get(selected.id) ?? selected.rowVersion, trusts: planning }),
				},
				staff.csrfToken,
			);
			await openIntake(selected.id);
			showMessage('Trust and beneficiary information saved.', 'confirmation');
		} catch (error) {
			showMessage(friendlyStaffError(error, 'Trust information was not saved'), 'error');
		}
	}

	async function updateDetails(input: { reminderLanguage: 'en' | 'ru' | 'both' }) {
		if (!selected || !staff) return;
		try {
			await api(`/api/staff/intakes/${selected.id}`, { method: 'PATCH', body: JSON.stringify(input) }, staff.csrfToken);
			await openIntake(selected.id);
			await loadIntakes();
			showMessage('Communication preferences saved.', 'confirmation');
		} catch (error) {
			showMessage(friendlyStaffError(error, 'Communication preferences were not saved'), 'error');
		}
	}

	async function archiveSelected() {
		if (!selected || !staff) return;
		const intakeId = selected.id;
		try {
			showMessage('Archiving intake...');
			await staffSaveChain.current.catch(() => undefined);
			await api(`/api/staff/intakes/${intakeId}/archive`, { method: 'POST', body: '{}' }, staff.csrfToken);
			setArchivedSearch('');
			setIntakeView('archived');
			await Promise.all([loadIntakes(), loadArchivedIntakes('')]);
			await openIntake(intakeId);
			showMessage('Intake archived. Its records and PDFs were retained, and client access was revoked.', 'confirmation');
		} catch (error) {
			showMessage(friendlyStaffError(error, 'Intake was not archived'), 'error');
		}
	}

	async function reopenSelected() {
		if (!selected || !staff) return;
		const intakeId = selected.id;
		try {
			showMessage('Reopening intake...');
			await api(`/api/staff/intakes/${intakeId}/reopen`, { method: 'POST', body: '{}' }, staff.csrfToken);
			setIntakeView('active');
			await Promise.all([loadIntakes(), loadArchivedIntakes(archivedSearch)]);
			await openIntake(intakeId);
			showMessage('Intake reopened. Create a new private link if the client needs access again.', 'confirmation');
		} catch (error) {
			showMessage(friendlyStaffError(error, 'Intake was not reopened'), 'error');
		}
	}

	async function updateLegalHold(enabled: boolean, reason: string) {
		if (!selected || !staff) return;
		try {
			showMessage(enabled ? 'Placing legal hold...' : 'Releasing legal hold...');
			await api(
				`/api/staff/intakes/${selected.id}/legal-hold`,
				{ method: 'POST', body: JSON.stringify({ enabled, reason }) },
				staff.csrfToken,
			);
			await Promise.all([openIntake(selected.id), loadIntakes(), loadArchivedIntakes(archivedSearch)]);
			showMessage(enabled ? 'Legal hold placed. Permanent deletion is blocked.' : 'Legal hold released.', 'confirmation');
		} catch (error) {
			showMessage(friendlyStaffError(error, 'Legal-hold status was not saved'), 'error');
		}
	}

	async function permanentlyDeleteSelected(confirmation: string, reason: string) {
		if (!selected || !staff) return;
		const intakeId = selected.id;
		try {
			showMessage('Permanently deleting intake...');
			await api(
				`/api/staff/intakes/${intakeId}/permanent-delete`,
				{ method: 'POST', body: JSON.stringify({ confirmation, reason }) },
				staff.csrfToken,
			);
			setSelected(null);
			intakeSelection.current = { id: null };
			setPlanning([]);
			await Promise.all([loadIntakes(), loadArchivedIntakes(archivedSearch), loadDeletionReceipts()]);
			showMessage(
				'Intake permanently deleted. The deletion receipt was retained; locked backup copies expire under the approved 30/90 policy.',
				'confirmation',
			);
		} catch (error) {
			showMessage(friendlyStaffError(error, 'Intake was not permanently deleted'), 'error');
		}
	}

	async function manageReminder(reminderId: string, action: 'cancel' | 'send-now') {
		if (!selected || !staff) return;
		try {
			await api(`/api/staff/intakes/${selected.id}/reminders/${reminderId}/${action}`, { method: 'POST', body: '{}' }, staff.csrfToken);
			await openIntake(selected.id);
			showMessage(
				reminderActionConfirmation(action, systemStatus?.deliveryMode, selected.reminders.find((item) => item.id === reminderId)?.channel),
				'confirmation',
			);
		} catch (error) {
			showMessage(friendlyStaffError(error, 'Reminder action failed'), 'error');
		}
	}

	async function recordTextConsent(method: 'verbal' | 'written' | 'web') {
		if (!selected || !staff) return;
		try {
			await api(
				`/api/staff/intakes/${selected.id}/sms-consent`,
				{ method: 'POST', body: JSON.stringify({ confirmed: true, method }) },
				staff.csrfToken,
			);
			await openIntake(selected.id);
			showMessage('Text-message consent recorded.', 'confirmation');
		} catch (error) {
			showMessage(friendlyStaffError(error, 'Text-message consent was not recorded'), 'error');
		}
	}

	async function retryPdfDelivery(pdfId: string) {
		if (!selected || !staff) return;
		try {
			await api(`/api/staff/intakes/${selected.id}/pdfs/${pdfId}/deliver`, { method: 'POST', body: '{}' }, staff.csrfToken);
			await openIntake(selected.id);
			showMessage('PDF delivery retry queued.', 'confirmation');
		} catch (error) {
			showMessage(friendlyStaffError(error, 'PDF delivery was not queued'), 'error');
		}
	}

	if (!staff)
		return (
			<main id="main" className="centered-panel">
				<h1>{FIRM_NAME} Staff Intake</h1>
				<p role="status">{message}</p>
			</main>
		);
	return (
		<>
			<header className="app-header staff-header">
				<div>
					<span className="eyebrow">{FIRM_NAME}</span>
					<h1>Estate Planning Intake</h1>
					<p>{staffIdentityLabel(staff)}</p>
				</div>
				{staff.role === 'administrator' && (
					<a className="button-link header-link" href="/staff/admin">
						Administration
					</a>
				)}
			</header>
			<main id="main" className="staff-shell">
				{message && (
					<p
						ref={messageRef}
						className={`staff-notice staff-notice-${messageKind}`}
						role={messageKind === 'error' ? 'alert' : 'status'}
						aria-live={messageKind === 'error' ? 'assertive' : 'polite'}
						aria-atomic="true"
						tabIndex={messageKind === 'confirmation' ? -1 : undefined}
					>
						{message}
					</p>
				)}
				{privateUrl && (
					<div ref={privateLinkRef} className="private-link" tabIndex={-1}>
						<strong>
							Private client link <span className="copy-save-warning">(COPY and SAVE)</span>
						</strong>
						<input readOnly value={privateUrl} aria-label="Private client link" />
						<button type="button" onClick={() => void navigator.clipboard.writeText(privateUrl)}>
							Copy link
						</button>
						<p>
							Copy and save this link now. It opens the client’s intake form and is shown only after creation. Treat it as confidential.
						</p>
					</div>
				)}
				<div className="staff-columns">
					<aside>
						<SystemStatusSummary payload={systemStatus} onRefresh={loadSystemStatus} />
						{canCreateIntake && (
							<NewIntakeForm
								onSubmit={create}
								disabled={systemStatus?.settings.intake_creation_enabled === false}
								smsEnabled={systemStatus?.smsDeliveryEnabled === true}
							/>
						)}
						<CollapsiblePanel className="intake-browser" title={intakeView === 'active' ? 'Active intakes' : 'Archived intakes'}>
							<div className="panel-heading">
								<button
									type="button"
									className="text-button"
									onClick={() => void (intakeView === 'active' ? loadIntakes() : loadArchivedIntakes())}
								>
									Refresh
								</button>
							</div>
							<div className="intake-view-switch" aria-label="Intake views">
								<button
									type="button"
									className={intakeView === 'active' ? undefined : 'secondary'}
									aria-pressed={intakeView === 'active'}
									onClick={() => void switchIntakeView('active')}
								>
									Active
								</button>
								<button
									type="button"
									className={intakeView === 'archived' ? undefined : 'secondary'}
									aria-pressed={intakeView === 'archived'}
									onClick={() => void switchIntakeView('archived')}
								>
									Archived
								</button>
							</div>
							{intakeView === 'active' ? (
								<>
									<label className="search-label">
										Search active intakes
										<input
											type="search"
											value={search}
											onChange={(event) => setSearch(event.target.value)}
											placeholder="Name, matter reference, email, phone, date, or status"
										/>
									</label>
									<IntakeList intakes={filteredIntakes} emptyMessage="No active intakes found." onOpen={openIntake} />
								</>
							) : (
								<>
									<form
										className="archive-search"
										onSubmit={(event) => {
											event.preventDefault();
											void loadArchivedIntakes();
										}}
									>
										<label className="search-label">
											Search archived intakes
											<input
												type="search"
												value={archivedSearch}
												onChange={(event) => setArchivedSearch(event.target.value)}
												placeholder="Name, matter reference, email, phone, or date"
												maxLength={200}
											/>
										</label>
										<button type="submit">Search</button>
									</form>
									<IntakeList intakes={archivedIntakes} emptyMessage="No archived intakes found." onOpen={openIntake} />
									{staff && (staff.role === 'administrator' || staff.role === 'attorney') && (
										<DeletionHistory deletions={deletionReceipts} />
									)}
								</>
							)}
						</CollapsiblePanel>
					</aside>
					<div className="staff-detail">
						{selected ? (
							<IntakeWorkspace
								key={selected.id}
								intake={selected}
								deliveryMode={systemStatus?.deliveryMode ?? 'record_only'}
								smsEnabled={systemStatus?.smsDeliveryEnabled === true}
								planning={planning}
								setPlanning={setPlanning}
								onAnswerChange={staffAnswerChange}
								onSend={sendOrReplace}
								onAddText={addText}
								onFinalize={finalize}
								onPrepareDocument={prepareDocument}
								onSavePlanning={savePlanning}
								onUpdateDetails={updateDetails}
								onManageReminder={manageReminder}
								onRecordTextConsent={recordTextConsent}
								onRetryPdf={retryPdfDelivery}
								onRefresh={async () => {
									await Promise.all([openIntake(selected.id), loadIntakes(), loadArchivedIntakes(archivedSearch)]);
								}}
								onArchive={archiveSelected}
								onReopen={reopenSelected}
								onSetLegalHold={updateLegalHold}
								onPermanentDelete={permanentlyDeleteSelected}
								canViewCoding={staff.role === 'administrator' || staff.role === 'attorney'}
								canCode={(staff.role === 'administrator' || staff.role === 'attorney') && selected.status !== 'archived'}
								canGeneratePdf={canGeneratePdf(staff.role, selected.status)}
								canUseWordDocuments={canUseWordDocuments(staff.role)}
								csrfToken={staff.csrfToken}
								canEdit={staff.role !== 'read_only' && selected.status !== 'archived' && !intakeSelection.current.loading}
								canReopen={staff.role !== 'read_only' && selected.status === 'archived'}
								canManageRecords={staff.role === 'administrator' || staff.role === 'attorney'}
							/>
						) : (
							<section className="panel empty-state">
								<h2>Select an intake</h2>
								<p>{canCreateIntake ? 'Open an existing intake or create a new one.' : 'Open an existing intake to view it.'}</p>
							</section>
						)}
					</div>
				</div>
			</main>
		</>
	);
}

function SystemStatusSummary({ payload, onRefresh }: { payload: AdministratorSettingsPayload | null; onRefresh: () => Promise<void> }) {
	return (
		<CollapsiblePanel className="system-status-summary" title="System status" defaultOpen={false}>
			<div className="panel-heading">
				<button type="button" className="text-button" onClick={() => void onRefresh()}>
					Refresh
				</button>
			</div>
			{payload ? (
				<ul className="system-status-list">
					<li>
						<span>Intake creation</span>
						<strong>{payload.settings.intake_creation_enabled ? 'Enabled' : 'Disabled'}</strong>
					</li>
					<li>
						<span>Client access</span>
						<strong>{payload.settings.client_access_enabled ? 'Enabled' : 'Disabled'}</strong>
					</li>
					<li>
						<span>Outbound delivery</span>
						<strong>{payload.settings.outbound_delivery_enabled ? 'Enabled' : 'Disabled'}</strong>
					</li>
				</ul>
			) : (
				<p>Loading system status…</p>
			)}
		</CollapsiblePanel>
	);
}

function IntakeList({
	intakes,
	emptyMessage,
	onOpen,
}: {
	intakes: IntakeSummary[];
	emptyMessage: string;
	onOpen: (id: string) => Promise<void>;
}) {
	if (!intakes.length) return <p className="list-empty">{emptyMessage}</p>;
	return (
		<ul className="intake-list">
			{intakes.map((intake) => (
				<li key={intake.id}>
					<button type="button" onClick={() => void onOpen(intake.id)}>
						<strong>{intake.clientDisplayName}</strong>
						{intake.matterReference && <span>Matter: {intake.matterReference}</span>}
						<span>
							{intakeStatusLabel(intake)} · {intake.missingPriorityCount} important blanks
						</span>
						{intake.status === 'archived' && (
							<span>
								Archived: {intake.archivedAt ? new Date(intake.archivedAt).toLocaleString() : 'date unavailable'} · Last activity:{' '}
								{new Date(intake.updatedAt).toLocaleString()}
							</span>
						)}
						{intake.legalHold && <span className="hold-label">Legal hold active</span>}
					</button>
				</li>
			))}
		</ul>
	);
}

function DeletionHistory({ deletions }: { deletions: IntakeDeletionReceipt[] }) {
	return (
		<details className="deletion-history">
			<summary>Permanent deletion history ({deletions.length})</summary>
			{deletions.length ? (
				<ul>
					{deletions.map((deletion) => (
						<li key={deletion.id}>
							<strong>{deletion.clientDisplayName}</strong>
							{deletion.matterReference && <> · Matter: {deletion.matterReference}</>}
							<br />
							<small>
								Deleted {new Date(deletion.completedAt).toLocaleString()} · Authorized by {deletion.authorizedBy} · Performed by{' '}
								{deletion.performedBy}
							</small>
							<p>Reason: {deletion.reason}</p>
						</li>
					))}
				</ul>
			) : (
				<p>No permanent deletions recorded.</p>
			)}
		</details>
	);
}

function IntakeWorkspace(props: {
	intake: IntakeDetail;
	deliveryMode: 'record_only' | 'provider';
	smsEnabled: boolean;
	planning: PlanningTrust[];
	setPlanning: (value: PlanningTrust[]) => void;
	onAnswerChange: (fieldId: string, value: JsonValue) => Promise<void>;
	onSend: () => Promise<void>;
	onAddText: (path: 'notes' | 'coding-instructions', body: string) => Promise<void>;
	onFinalize: () => Promise<void>;
	onPrepareDocument: (intakeId: string) => Promise<() => boolean>;
	onSavePlanning: () => Promise<void>;
	onUpdateDetails: (input: { reminderLanguage: 'en' | 'ru' | 'both' }) => Promise<void>;
	onManageReminder: (reminderId: string, action: 'cancel' | 'send-now') => Promise<void>;
	onRecordTextConsent: (method: 'verbal' | 'written' | 'web') => Promise<void>;
	onRetryPdf: (pdfId: string) => Promise<void>;
	onRefresh: () => Promise<void>;
	onArchive: () => Promise<void>;
	onReopen: () => Promise<void>;
	onSetLegalHold: (enabled: boolean, reason: string) => Promise<void>;
	onPermanentDelete: (confirmation: string, reason: string) => Promise<void>;
	canViewCoding: boolean;
	canCode: boolean;
	canGeneratePdf: boolean;
	canUseWordDocuments: boolean;
	csrfToken: string;
	canEdit: boolean;
	canReopen: boolean;
	canManageRecords: boolean;
}) {
	const [note, setNote] = useState('');
	const [code, setCode] = useState('');
	const [consentMethod, setConsentMethod] = useState<'verbal' | 'written' | 'web'>('verbal');
	const [holdReason, setHoldReason] = useState('');
	const [deletionConfirmation, setDeletionConfirmation] = useState('');
	const [deletionReason, setDeletionReason] = useState('');
	return (
		<div className="workspace-stack">
			<section className="panel">
				{props.intake.status === 'archived' && (
					<div className="archived-notice">
						<div>
							<strong>Archived intake — read only</strong>
							<p>Reopen this intake before making changes. Revoked client links will remain revoked.</p>
							<p>
								Archived: {props.intake.archivedAt ? new Date(props.intake.archivedAt).toLocaleString() : 'date unavailable'} · Last
								activity: {new Date(props.intake.updatedAt).toLocaleString()}
							</p>
						</div>
						{props.canReopen && (
							<button type="button" onClick={() => void props.onReopen()}>
								Reopen intake
							</button>
						)}
					</div>
				)}
				{props.intake.legalHold && (
					<div className="legal-hold-notice">
						<strong>Legal hold active — permanent deletion is blocked</strong>
						<p>
							Placed {new Date(props.intake.legalHold.placedAt).toLocaleString()} by {props.intake.legalHold.placedBy}. Reason:{' '}
							{props.intake.legalHold.reason}
						</p>
					</div>
				)}
				<div className="panel-heading">
					<div>
						<h2>{props.intake.clientDisplayName}</h2>
						<p>
							{intakeStatusLabel(props.intake)}
							{props.intake.consultationAt && <> · Consultation: {new Date(props.intake.consultationAt).toLocaleString()}</>}
						</p>
					</div>
					<div className="button-row">
						<button type="button" className="secondary" onClick={() => void props.onRefresh()}>
							Refresh
						</button>
						{props.canEdit && (
							<button type="button" onClick={() => void props.onSend()}>
								Send or replace link
							</button>
						)}
					</div>
				</div>
				<h3>Important answers to discuss</h3>
				{props.intake.missingPriority.length ? (
					<ul>
						{props.intake.missingPriority.map((item) => (
							<li key={item}>{item}</li>
						))}
					</ul>
				) : (
					<p>No important answers are currently blank.</p>
				)}
				<h3>Conflict-review names</h3>
				<p>For staff review only; the system does not clear conflicts.</p>
				{props.intake.conflictNames.length ? (
					<ul>
						{props.intake.conflictNames.map((item, index) => (
							<li key={`${item.kind}-${item.name}-${index}`}>
								{item.name} ({item.kind.replaceAll('_', ' ')})
							</li>
						))}
					</ul>
				) : (
					<p>No names are currently available.</p>
				)}
			</section>
			{props.canEdit && (
				<section className="panel">
					<h2>Communication preferences</h2>
					<IntakeSettingsForm key={`${props.intake.id}-${props.intake.updatedAt}`} intake={props.intake} onSave={props.onUpdateDetails} />
				</section>
			)}
			{props.canManageRecords && (
				<section className="panel record-controls">
					<h2>Legal hold</h2>
					<p>
						{props.intake.legalHold
							? 'Release the hold only after confirming that the firm no longer has a legal or preservation obligation.'
							: 'Place a legal hold to prevent this intake from being permanently deleted.'}
					</p>
					<label>
						Reason
						<textarea value={holdReason} onChange={(event) => setHoldReason(event.target.value)} rows={3} maxLength={1000} />
					</label>
					<button
						type="button"
						className={props.intake.legalHold ? 'secondary' : undefined}
						disabled={!holdReason.trim()}
						onClick={() => {
							const action = props.intake.legalHold ? 'Release' : 'Place';
							if (window.confirm(`${action} the legal hold for this intake?`))
								void props.onSetLegalHold(!props.intake.legalHold, holdReason.trim());
						}}
					>
						{props.intake.legalHold ? 'Release legal hold' : 'Place legal hold'}
					</button>
				</section>
			)}
			<section className="panel">
				<h2>Questionnaire</h2>
				<QuestionnaireEditor
					answers={props.intake.answers}
					onChange={props.onAnswerChange}
					showProgress={false}
					disabled={!props.canEdit}
					fieldMeta={props.intake.answerAttribution}
				/>
			</section>
			<section className="panel">
				<h2>Initial Consultation: proposed trusts</h2>
				<p>
					Staff consultation decisions are recorded here. Client-proposed trustees and beneficiaries are shown separately in the
					questionnaire’s Personal directions section; saving either section does not overwrite the other.
				</p>
				<TrustPlanningEditor trusts={props.planning} setTrusts={props.setPlanning} disabled={!props.canCode} />
				{props.canCode && (
					<button type="button" onClick={() => void props.onSavePlanning()}>
						Save trust information
					</button>
				)}
			</section>
			<section className="panel">
				<h2>Consultation notes</h2>
				{props.canEdit && (
					<>
						<textarea value={note} onChange={(event) => setNote(event.target.value)} rows={5} />
						<button
							type="button"
							onClick={() =>
								void props
									.onAddText('notes', note)
									.then(() => setNote(''))
									.catch(() => undefined)
							}
						>
							Add note
						</button>
					</>
				)}
				{props.intake.notes.map((item) => (
					<article className="history-item" key={item.id}>
						<p>{item.body}</p>
						<small>
							{item.createdBy} · {new Date(item.createdAt).toLocaleString()}
						</small>
					</article>
				))}
			</section>
			{props.canViewCoding && (
				<section className="panel">
					<h2>Coding instructions</h2>
					{props.canCode && (
						<>
							<textarea value={code} onChange={(event) => setCode(event.target.value)} rows={5} />
							<button
								type="button"
								onClick={() =>
									void props
										.onAddText('coding-instructions', code)
										.then(() => setCode(''))
										.catch(() => undefined)
								}
							>
								Add coding instructions
							</button>
						</>
					)}
					{props.intake.codingInstructions.map((item) => (
						<article className="history-item" key={item.id}>
							<p>{item.body}</p>
							<small>
								{item.createdBy} · {new Date(item.createdAt).toLocaleString()}
							</small>
						</article>
					))}
				</section>
			)}
			<section className="panel">
				<h2>Reminders</h2>
				<p>
					{props.deliveryMode === 'record_only'
						? 'Test mode records reminder activity but does not contact real recipients.'
						: props.smsEnabled
							? 'Invitations and reminders use the selected delivery channels when outbound delivery is enabled.'
							: 'Invitations and reminders are sent by email.'}
				</p>
				{props.intake.smsConsent && (
					<p>
						Text consent: {props.intake.smsConsent.status === 'opted_out' ? 'Opted out' : 'Active'} · {props.intake.smsConsent.method} ·{' '}
						{props.intake.smsConsent.destinationMasked} · recorded {new Date(props.intake.smsConsent.consentedAt).toLocaleString()} by{' '}
						{props.intake.smsConsent.recordedBy} · disclosure {props.intake.smsConsent.disclosureVersion}
					</p>
				)}
				{props.smsEnabled && props.canEdit && props.intake.clientMobile && !props.intake.smsConsent && (
					<div>
						<p>
							No current text-message consent record exists for this intake. Before recording consent, confirm the client expressly agreed
							to receive automated questionnaire links and appointment reminders from {FIRM_NAME} at this number; message and data rates may
							apply; consent is not a condition of legal services; and the client was told to reply STOP to opt out or HELP for help.
						</p>
						<label>
							How consent was obtained
							<select value={consentMethod} onChange={(event) => setConsentMethod(event.target.value as typeof consentMethod)}>
								<option value="verbal">Verbally</option>
								<option value="written">In writing</option>
								<option value="web">Through a web form</option>
							</select>
						</label>
						<button type="button" onClick={() => void props.onRecordTextConsent(consentMethod)}>
							Confirm and record text consent
						</button>
					</div>
				)}
				<MessagePreviews intake={props.intake} smsEnabled={props.smsEnabled} />
				{props.intake.reminders.length ? (
					<ul className="reminder-list">
						{props.intake.reminders.map((reminder) => (
							<li key={reminder.id}>
								<div>
									<strong>{reminder.type.replaceAll('_', ' ')}</strong> · {reminder.channel} · {reminder.language} ·{' '}
									{reminderStatusLabel(reminder.status, reminder.deliveryKind)}
									<br />
									<small>
										{new Date(reminder.scheduledFor).toLocaleString()} · {reminder.attemptCount} attempts
										{reminder.providerStatus && ` · provider: ${reminder.providerStatus}`}
										{reminder.providerErrorCode && ` · provider error: ${reminder.providerErrorCode}`}
									</small>
								</div>
								{props.canEdit && ['scheduled', 'failed'].includes(reminder.status) && (
									<div className="button-row">
										<button type="button" className="secondary" onClick={() => void props.onManageReminder(reminder.id, 'send-now')}>
											Send now
										</button>
										<button type="button" className="secondary" onClick={() => void props.onManageReminder(reminder.id, 'cancel')}>
											Cancel
										</button>
									</div>
								)}
								{props.canEdit && reminder.status === 'cancelled' && (
									<button type="button" className="secondary" onClick={() => void props.onManageReminder(reminder.id, 'send-now')}>
										Send now
									</button>
								)}
								{props.canEdit && reminder.status === 'queued' && (
									<button type="button" className="secondary" onClick={() => void props.onManageReminder(reminder.id, 'cancel')}>
										Cancel
									</button>
								)}
							</li>
						))}
					</ul>
				) : (
					<p>No reminders are recorded.</p>
				)}
			</section>
			<section className="panel">
				<div className="panel-heading">
					<div>
						<h2>Printable questionnaire</h2>
						<p>Each generated PDF is preserved as a separate version.</p>
					</div>
					{props.canGeneratePdf && (
						<button type="button" onClick={() => void props.onFinalize()}>
							Create new PDF
						</button>
					)}
				</div>
				{props.intake.pdfs.length ? (
					<ul>
						{props.intake.pdfs.map((pdf) => (
							<li key={pdf.id}>
								<a href={`/api/staff/intakes/${props.intake.id}/pdfs/${pdf.id}`}>Download version {pdf.versionNumber}</a>
								{pdfDeliveryLabel(pdf.deliveryStatus)} · {new Date(pdf.generatedAt).toLocaleString()}
								{props.canCode && pdf.deliveryStatus === 'failed' && (
									<>
										{' · '}
										<button type="button" className="text-button" onClick={() => void props.onRetryPdf(pdf.id)}>
											Retry delivery
										</button>
									</>
								)}
							</li>
						))}
					</ul>
				) : (
					<p>No PDFs generated yet.</p>
				)}
			</section>
			{props.canUseWordDocuments && (
				<WordDocuments
					key={props.intake.id}
					intake={props.intake}
					csrfToken={props.csrfToken}
					onRefresh={props.onRefresh}
					trusts={props.planning}
					beforeCreate={props.onPrepareDocument}
				/>
			)}
			{props.canEdit && (
				<section className="panel intake-actions">
					<h2>Intake actions</h2>
					<p>Archiving removes this intake from the active list. Its records and PDFs are retained, while client access is revoked.</p>
					<button
						type="button"
						className="danger-button"
						onClick={() => {
							if (window.confirm('Archive this intake? Its records and PDFs will be retained, and client access will be revoked.'))
								void props.onArchive();
						}}
					>
						Archive intake
					</button>
				</section>
			)}
			{props.canManageRecords && props.intake.status === 'archived' && (
				<section className="panel permanent-deletion">
					<h2>Permanently delete intake</h2>
					<p>
						This removes the questionnaire, client links, working PDFs, notes, and related records. It cannot be undone from the live
						system. A minimal deletion receipt remains. Locked backups remain for 30 days and expire after 90 days.
					</p>
					{props.intake.legalHold ? (
						<p className="hold-label">Deletion is unavailable while the legal hold is active.</p>
					) : (
						<form
							className="compact-form"
							onSubmit={(event) => {
								event.preventDefault();
								if (window.confirm(`Permanently delete ${props.intake.clientDisplayName}? This cannot be undone from the live system.`))
									void props.onPermanentDelete(deletionConfirmation.trim(), deletionReason.trim());
							}}
						>
							<label>
								Reason for permanent deletion
								<textarea
									value={deletionReason}
									onChange={(event) => setDeletionReason(event.target.value)}
									rows={3}
									maxLength={1000}
									required
								/>
							</label>
							<label>
								Type the client name exactly to confirm: <strong>{props.intake.clientDisplayName}</strong>
								<input
									value={deletionConfirmation}
									onChange={(event) => setDeletionConfirmation(event.target.value)}
									maxLength={200}
									required
								/>
							</label>
							<button
								type="submit"
								className="danger-button"
								disabled={deletionConfirmation.trim() !== props.intake.clientDisplayName || !deletionReason.trim()}
							>
								Permanently delete intake
							</button>
						</form>
					)}
				</section>
			)}
		</div>
	);
}

function TrustPlanningEditor({
	trusts,
	setTrusts,
	disabled,
}: {
	trusts: PlanningTrust[];
	setTrusts: (value: PlanningTrust[]) => void;
	disabled: boolean;
}) {
	const update = (index: number, patch: Partial<PlanningTrust>) =>
		setTrusts(trusts.map((trust, trustIndex) => (trustIndex === index ? { ...trust, ...patch } : trust)));
	return (
		<div>
			{trusts.map((trust, index) => (
				<div className="repeat-item" key={trust.id ?? index}>
					<div className="repeat-heading">
						<strong>Proposed trust {index + 1}</strong>
						<button
							type="button"
							className="text-button"
							disabled={disabled}
							onClick={() => setTrusts(trusts.filter((_, trustIndex) => trustIndex !== index))}
						>
							Remove
						</button>
					</div>
					<label>
						Trust name or reference
						<input
							value={trust.referenceName ?? ''}
							disabled={disabled}
							onChange={(event) => update(index, { referenceName: event.target.value })}
						/>
					</label>
					<label>
						Trust type
						<select
							value={trust.trustType ?? ''}
							disabled={disabled}
							onChange={(event) => update(index, { trustType: (event.target.value || null) as PlanningTrust['trustType'] })}
						>
							<option value="">Select</option>
							<option value="revocable">Revocable</option>
							<option value="irrevocable">Irrevocable</option>
							<option value="other">Other</option>
						</select>
					</label>
					<label>
						First-choice trustee
						<NameInput
							value={trust.trustees?.primary?.fullName ?? ''}
							disabled={disabled}
							onValueChange={(value) =>
								update(index, { trustees: { ...trust.trustees, primary: { ...trust.trustees?.primary, fullName: value } } })
							}
						/>
					</label>
					<label>
						Successor trustee
						<NameInput
							value={trust.trustees?.successor?.fullName ?? ''}
							disabled={disabled}
							onValueChange={(value) =>
								update(index, {
									trustees: { ...trust.trustees, successor: { ...trust.trustees?.successor, fullName: value } },
								})
							}
						/>
					</label>
					<label>
						Primary beneficiaries (one per line)
						<NameTextarea
							value={namesToText(trust.primaryBeneficiaries)}
							disabled={disabled}
							onValueChange={(value) => update(index, { primaryBeneficiaries: textToNames(value) })}
						/>
					</label>
					<label>
						Successor beneficiaries (one per line)
						<NameTextarea
							value={namesToText(trust.successorBeneficiaries)}
							disabled={disabled}
							onValueChange={(value) => update(index, { successorBeneficiaries: textToNames(value) })}
						/>
					</label>
					<label>
						Contingent beneficiaries (one per line)
						<NameTextarea
							value={namesToText(trust.contingentBeneficiaries)}
							disabled={disabled}
							onValueChange={(value) => update(index, { contingentBeneficiaries: textToNames(value) })}
						/>
					</label>
					<label>
						Funding notes
						<textarea
							value={trust.fundingNotes ?? ''}
							disabled={disabled}
							onChange={(event) => update(index, { fundingNotes: event.target.value })}
						/>
					</label>
				</div>
			))}
			<button
				type="button"
				className="secondary"
				disabled={disabled}
				onClick={() => setTrusts([...trusts, { primaryBeneficiaries: [], successorBeneficiaries: [], contingentBeneficiaries: [] }])}
			>
				Add proposed trust
			</button>
		</div>
	);
}

function namesToText(items: Array<{ displayName: string }>): string {
	return items.map((item) => item.displayName).join('\n');
}
function textToNames(value: string): Array<{ displayName: string; entityType: 'person' }> {
	return value
		.split('\n')
		.map((item) => item.trim())
		.filter(Boolean)
		.map((displayName) => ({ displayName, entityType: 'person' }));
}
function emptyToNull(value: FormDataEntryValue | null): string | null {
	const text = typeof value === 'string' ? value.trim() : '';
	return text || null;
}
